import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { LongText, SpaceBetweenBox } from "components/GeneralStyled";
import {
  SendEmailForWithdraw,
  SendSMSForWithdraw,
  WithdrawConfirm,
} from "store/api/withdraw";
import {
  getHistoryCryptoList,
  getHistoryFiatList,
} from "store/slices/wallet/walletSlice";
import { useEffect, useState } from "react";

import CountdownButton from "components/CountdownButton";
import { DefaultUrlWeb } from "config/Configuration";
import Modal from "components/Modal";
import NumberFormats from "components/NumberFormats";
import TimeoutButton from "components/TimeoutButton";
import { allowMaxCharLength } from "utils/helpers/allowMaxCharLength";
import { formatDateToJalali } from "utils/helpers/time";
import http from "config/http";
import useDispatch from "hooks/useDispatch";
import { useForm } from "react-hook-form";
import useNotify from "hooks/useNotify";
import { useSearchParams } from "react-router-dom";

function ConfirmWithdrawModal({
  open,
  onClose,
  withdrawInitialConfirm,
  setWithdrawResult,
  setOpenConfirmationModal,
  address,
}) {
  const notify = useNotify();

  const { handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      requestId: withdrawInitialConfirm?.id,
      smsCode: "",
      emailCode: "",
      appCode: "",
    },
  });

  const [startTimer, setStartTimer] = useState(false);
  const [loading, setLoading] = useState(false) 
  const [smsButtonLoading , setSmsButtonLoading] = useState(false)
  const [emailButtonLoading , setEmailButtonLoading] = useState(false)

  const handleGetSMSCode = async (startCountDown) => {
    try {
      setSmsButtonLoading(true)
      await http.post(SendSMSForWithdraw, {
        requestId: withdrawInitialConfirm?.id,
      });
      
      setSmsButtonLoading(false)
      startCountDown();
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const handleGetEmailCode = async (startCountDown) => {
    try {
      setEmailButtonLoading(true)
      await http.post(SendEmailForWithdraw, {
        requestId: withdrawInitialConfirm?.id,
      });
      
      setEmailButtonLoading(false)
      startCountDown();
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const coinId = +searchParams.get("coinId") || -1;
  const sideId = +searchParams.get("sideId") || 0;
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const pageNumber = searchParams.get("page") || 1;
  const pageSize = searchParams.get("perPage") || 10;

  const isDigital =
    searchParams.get("isDigital")?.toLocaleLowerCase() !== "false";

  const getHistoryOfWithdrawOrDeposit = async () => {
    const body = {
      coinId,
      sideId,
      startDate: formatDateToJalali(startDate),
      endDate: formatDateToJalali(endDate),
      pageSize,
      pageNumber,
    };

    if (isDigital) {
      await dispatch(getHistoryCryptoList(body));
    } else {
      await dispatch(getHistoryFiatList(body));
    }
  };

  const onConfirm = async (body) => {
    try {
      setLoading(true);
      const { data } = await http.post(WithdrawConfirm, body);

      setWithdrawResult(data?.data);
      getHistoryOfWithdrawOrDeposit();
      setOpenConfirmationModal(true);
      onClose();
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }

    setLoading(false)
  };

  useEffect(() => {
    if (open) {
      setStartTimer(true);
    } else {
      setStartTimer(false);
    }
  }, [open]);

  const buttonIsDisabled = () => {
    const fieldsThatShouldBeFilled = ["emailCode"];

    if (withdrawInitialConfirm?.isShowSMS)
      fieldsThatShouldBeFilled.push("smsCode");
    if (withdrawInitialConfirm?.isShowApp)
      fieldsThatShouldBeFilled.push("appCode");

    const checkIfAllFieldsAreFilled = fieldsThatShouldBeFilled.every(
      (field) => watch(field) !== ""
    );

    return !checkIfAllFieldsAreFilled;
  };

  return (
    <Modal
      maxWidth={"sm"}
      onClose={onClose}
      open={open}
      title="Withdrawal"
      dir="ltr"
      persistent
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <CoinNameWrapper>
            <img
              src={DefaultUrlWeb + withdrawInitialConfirm?.coinImageUrl}
              alt={withdrawInitialConfirm?.coinName}
            />

            <Typography variant="h5">
              {withdrawInitialConfirm?.coinName}
            </Typography>
          </CoinNameWrapper>
        </Grid>

        <Grid item xs={12}>
          <SpaceBetweenBox>
            <Typography color="text.secondary" variant="h6">
              Amount
            </Typography>

            <Typography
              textAlign="right"
              component="b"
              color="text.main"
              variant="h5"
            >
              <NumberFormats
                value={withdrawInitialConfirm?.amountOriginal || 0}
              />{" "}
              <Typography
                variant="subtitle1"
                color="text.secondary"
                component="small"
              >
                {withdrawInitialConfirm?.coinName}
              </Typography>
            </Typography>
          </SpaceBetweenBox>

          {withdrawInitialConfirm?.fee !== 0 && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Fee
              </Typography>
              <LongText
                textAlign="right"
                color="text.main"
                variant="subtitle1"
              >
                {withdrawInitialConfirm?.fee}
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  component="small"
                  pl={0.5}
                >
                  {withdrawInitialConfirm?.coinName}
                </Typography>
              </LongText>
            </SpaceBetweenBox>
          )}

          {withdrawInitialConfirm?.amount && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Receive Amount
              </Typography>
              <Typography
                textAlign="right"
                component="b"
                color="text.main"
                variant="h5"
              >
                <NumberFormats value={withdrawInitialConfirm?.amount} />
                <Typography
                  pl={0.5}
                  variant="subtitle1"
                  color="text.secondary"
                  component="small"
                >
                  {withdrawInitialConfirm?.coinName}
                </Typography>
              </Typography>
            </SpaceBetweenBox>
          )}

          {withdrawInitialConfirm?.userNameSide && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Username side
              </Typography>
              <LongText
                textAlign="right"
                color="text.secondary"
                variant="subtitle1"
              >
                {withdrawInitialConfirm?.userNameSide}
              </LongText>
            </SpaceBetweenBox>
          )}

          {withdrawInitialConfirm?.address || address ? (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Address
              </Typography>
              <LongText
                textAlign="right"
                color="text.main"
                variant="subtitle1"
              >
                {withdrawInitialConfirm?.address || address}
              </LongText>
            </SpaceBetweenBox>
          ) : (
            ""
          )}

          {withdrawInitialConfirm?.proposal && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="subtitle1">
                Proposal
              </Typography>
              <TypographyProposal
                textAlign="right"
                color="text.secondary"
                variant="h5"
              >
                {withdrawInitialConfirm?.proposal}
              </TypographyProposal>
            </SpaceBetweenBox>
          )}

          {withdrawInitialConfirm?.tagOrMemo && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Tag or Memo
              </Typography>
              <Typography textAlign="right" color="text.secondary" variant="h5">
                {withdrawInitialConfirm?.tagOrMemo}
              </Typography>
            </SpaceBetweenBox>
          )}

          {withdrawInitialConfirm?.type && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Type
              </Typography>
              <LongText
                textAlign="right"
                color="text.secondary"
                variant="subtitle1"
              >
                {withdrawInitialConfirm?.type}
              </LongText>
            </SpaceBetweenBox>
          )}

          {withdrawInitialConfirm?.network !== "Toman" && (
            <SpaceBetweenBox>
              <Typography color="text.secondary" variant="h6">
                Network
              </Typography>

              <LongText
                textAlign="right"
                color="text.secondary"
                variant="subtitle1"
              >
                {withdrawInitialConfirm?.network}
              </LongText>
            </SpaceBetweenBox>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography textAlign="right" variant="h6" color="text.main">
            Email: {withdrawInitialConfirm?.email}
          </Typography>

          <TextField
            dir="rtl"
            placeholder="کد ارسالی به ایمیل خود را وارد کنید"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <CountdownButton
                    onClick={handleGetEmailCode}
                    loading={emailButtonLoading}
                    time={withdrawInitialConfirm?.validSecondsSendEmail}
                  />
                </InputAdornment>
              ),
            }}
            value={watch("emailCode")}
            onChange={(e) =>
              allowMaxCharLength({
                name: "emailCode",
                value: e.target.value,
                setValue,
                length: 6,
              })
            }
          />
        </Grid>

        {withdrawInitialConfirm?.isShowSMS && (
          <Grid item xs={12}>
            <Typography textAlign="right" variant="h6" color="text.main">
              Phone: {withdrawInitialConfirm?.phoneNumber}
            </Typography>

            <TextField
              dir="rtl"
              placeholder="کد SMS ارسالی را وارد کنید"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CountdownButton
                      onClick={handleGetSMSCode}
                      loading={smsButtonLoading}
                      time={withdrawInitialConfirm?.validSecondsSendSMS}
                    />
                  </InputAdornment>
                ),
              }}
              value={watch("smsCode")}
              onChange={(e) =>
                allowMaxCharLength({
                  name: "smsCode",
                  value: e.target.value,
                  setValue,
                  length: 6,
                })
              }
            />
          </Grid>
        )}

        {withdrawInitialConfirm?.isShowApp && (
          <Grid item xs={12}>
            <TextField
              dir="rtl"
              placeholder="کد Authentication App را وارد کنید"
              value={watch("appCode")}
              onChange={(e) =>
                allowMaxCharLength({
                  name: "appCode",
                  value: e.target.value,
                  setValue,
                  length: 6,
                })
              }
            />
          </Grid>
        )}
      </Grid>

      <Grid container mt={2} spacing={1}>
        <Grid item xs={6}>
          <Button color="info" fullWidth onClick={onClose}>
            Cancel
          </Button>
        </Grid>

        <Grid item xs={6}>
          <TimeoutButton
            disabled={buttonIsDisabled()}
            start={startTimer}
            title="Confirm"
            time={withdrawInitialConfirm?.validSeconds}
            onComplete={onClose}
            fullWidth
            loading={loading}
            color="success"
            onClick={handleSubmit(onConfirm)}
          ></TimeoutButton>
        </Grid>
      </Grid>
    </Modal>
  );
}

const CoinNameWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  img: {
    marginRight: theme.spacing(1),
    width: "28px",
    height: "28px",
  },
}));

const TypographyProposal = styled(Typography)(() => ({
  width: "50%",
  fontSize: "12px",
  lineBreak: "anywhere",
}));

export default ConfirmWithdrawModal;
